import { useEffect, useState, useCallback } from 'react';
import { Mail, Loader2, Eye, RotateCcw, Save, Check } from 'lucide-react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [draft, setDraft] = useState({ subject: '', title: '', intro: '' });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewing, setPreviewing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listEmailTemplates();
      setTemplates(res.data);
      if (!selectedKey && res.data.length) selectTemplate(res.data[0]);
    } catch {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const selected = templates.find(t => t.key === selectedKey);

  const selectTemplate = (t) => {
    setSelectedKey(t.key);
    setDraft({ subject: t.subject, title: t.title, intro: t.intro });
    setPreviewHtml('');
    setPreviewSubject('');
  };

  const doPreview = async () => {
    if (!selectedKey) return;
    setPreviewing(true);
    try {
      const res = await adminAPI.previewEmailTemplate(selectedKey, draft);
      setPreviewHtml(res.data.html);
      setPreviewSubject(res.data.subject);
    } catch {
      toast.error('Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const doSave = async () => {
    if (!selectedKey) return;
    setSaving(true);
    try {
      await adminAPI.updateEmailTemplate(selectedKey, draft);
      toast.success('Template saved');
      await load();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const doReset = async () => {
    if (!selectedKey || !window.confirm('Reset this email to the default wording?')) return;
    setResetting(true);
    try {
      await adminAPI.resetEmailTemplate(selectedKey);
      const res = await adminAPI.listEmailTemplates();
      setTemplates(res.data);
      const fresh = res.data.find(t => t.key === selectedKey);
      if (fresh) setDraft({ subject: fresh.subject, title: fresh.title, intro: fresh.intro });
      setPreviewHtml('');
      toast.success('Reset to default');
    } catch {
      toast.error('Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const insertVar = (token) => {
    setDraft(d => ({ ...d, intro: `${d.intro}{${token}}` }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="email-templates-loading">
        <Loader2 className="h-6 w-6 animate-spin text-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-email-templates">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-teal/10">
          <Mail className="h-5 w-5 text-teal" strokeWidth={1.75} />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-light text-foreground">Email Templates</h1>
          <p className="font-body text-sm text-foreground/55">Customize the subject, heading and message of every customer email. Order details, totals and buttons are added automatically.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Template list */}
        <div className="space-y-1.5" data-testid="email-template-list">
          {templates.map(t => (
            <button
              key={t.key}
              data-testid={`email-template-item-${t.key}`}
              onClick={() => selectTemplate(t)}
              aria-pressed={selectedKey === t.key}
              className={`w-full text-left px-3.5 py-2.5 rounded-xl border-2 transition-all ${
                selectedKey === t.key
                  ? 'bg-teal/15 border-teal ring-2 ring-teal/40 shadow-sm'
                  : 'bg-card border-border hover:border-teal/40 hover:bg-teal/5'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className={`flex items-center gap-2 font-body text-sm ${selectedKey === t.key ? 'text-teal font-semibold' : 'text-foreground font-medium'}`}>
                  {selectedKey === t.key && <Check className="h-4 w-4 flex-shrink-0" strokeWidth={3} />}
                  {t.name}
                </span>
                {t.is_customized && (
                  <Badge className="bg-gold/15 text-gold border-gold/30 text-[10px] px-1.5 py-0">Edited</Badge>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Editor */}
        {selected && (
          <div className="space-y-5" data-testid="email-template-editor">
            <div className="rounded-2xl bg-card border border-border p-5 space-y-4">
              <p className="font-body text-xs text-foreground/50">{selected.description}</p>

              <div>
                <Label className="font-body text-xs text-foreground/60 font-medium">Subject line</Label>
                <Input
                  data-testid="email-subject-input"
                  value={draft.subject}
                  onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))}
                  className="rounded-xl bg-background border-border mt-1.5"
                />
              </div>

              <div>
                <Label className="font-body text-xs text-foreground/60 font-medium">Heading</Label>
                <Input
                  data-testid="email-title-input"
                  value={draft.title}
                  onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
                  className="rounded-xl bg-background border-border mt-1.5"
                />
              </div>

              <div>
                <Label className="font-body text-xs text-foreground/60 font-medium">Message</Label>
                <Textarea
                  data-testid="email-intro-input"
                  value={draft.intro}
                  onChange={e => setDraft(d => ({ ...d, intro: e.target.value }))}
                  rows={8}
                  className="rounded-xl bg-background border-border mt-1.5 font-body text-sm leading-relaxed"
                />
                <p className="font-body text-[11px] text-foreground/40 mt-1.5">Tip: leave a blank line between paragraphs.</p>
              </div>

              <div>
                <Label className="font-body text-[11px] text-foreground/50 font-medium uppercase tracking-wide">Available placeholders (click to insert)</Label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {selected.variables.map(v => (
                    <button
                      key={v.token}
                      type="button"
                      data-testid={`var-chip-${v.token}`}
                      onClick={() => insertVar(v.token)}
                      title={v.desc}
                      className="px-2 py-1 rounded-md bg-muted/60 hover:bg-teal/15 border border-border font-mono text-[11px] text-teal transition-colors"
                    >
                      {'{' + v.token + '}'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button onClick={doPreview} disabled={previewing} data-testid="email-preview-btn" variant="outline" className="rounded-full gap-1.5 h-9 text-xs">
                  {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Preview
                </Button>
                <Button onClick={doSave} disabled={saving} data-testid="email-save-btn" className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light gap-1.5 h-9 text-xs">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                </Button>
                <Button onClick={doReset} disabled={resetting || !selected.is_customized} data-testid="email-reset-btn" variant="ghost" className="rounded-full gap-1.5 h-9 text-xs text-foreground/60">
                  {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Reset to default
                </Button>
              </div>
            </div>

            {/* Preview */}
            {previewHtml && (
              <div className="rounded-2xl bg-card border border-border overflow-hidden" data-testid="email-preview-panel">
                <div className="px-5 py-3 border-b border-border bg-muted/30">
                  <p className="font-body text-[11px] text-foreground/45 uppercase tracking-wide flex items-center gap-1.5">
                    <Check className="h-3 w-3 text-teal" /> Live preview (sample data)
                  </p>
                  <p className="font-body text-sm text-foreground font-medium mt-1">
                    <span className="text-foreground/45">Subject: </span>{previewSubject}
                  </p>
                </div>
                <iframe
                  title="email-preview"
                  data-testid="email-preview-iframe"
                  srcDoc={previewHtml}
                  className="w-full"
                  style={{ height: '620px', border: 'none', background: '#ffffff' }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
